export enum OrderStatusEnum {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  PENDING = 'PENDING',
  PENDING_CANCELLED = 'PENDING_CANCELLED',
  ACCEPTED = 'ACCEPTED',
  CANCELLED = 'CANCELLED',
  ALLOTTED = 'ALLOTTED',
  EXECUTED = 'EXECUTED',
  SUBMITTED_AND_DRAFT = 'SUBMITTED_AND_DRAFT',
  SUBMITTED_INTERNAL_FAILURE = 'SUBMITTED_INTERNAL_FAILURE',
  SUBMITTED_EXTERNAL_FAILURE = 'SUBMITTED_EXTERNAL_FAILURE',
}

export const statusCanSubmit: Set<OrderStatusEnum> = new Set([
  OrderStatusEnum.DRAFT,
  OrderStatusEnum.SUBMITTED_AND_DRAFT,
]);

/** Order statuses treated as success for reconfirm/cancel (SUBMITTED, ACCEPTED). ALLOTTED/EXECUTED = completed, not included. */
export const successOrderStatuses: Set<OrderStatusEnum> = new Set([
  OrderStatusEnum.ACCEPTED,
  OrderStatusEnum.SUBMITTED,
]);

export const statusCanCancel: Set<OrderStatusEnum> = new Set([
  OrderStatusEnum.ACCEPTED,
  OrderStatusEnum.SUBMITTED,
  OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE,
  OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE,
]);

export type StatusWithIcon = Extract<
  OrderStatusEnum,
  | OrderStatusEnum.SUBMITTED_AND_DRAFT
  | OrderStatusEnum.PENDING
  | OrderStatusEnum.SUBMITTED
  | OrderStatusEnum.PENDING_CANCELLED
  | OrderStatusEnum.ACCEPTED
  | OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE
  | OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE
>;

export const OrderStatusIcon: Record<StatusWithIcon, string> = {
  [OrderStatusEnum.PENDING]: 'spinner',
  [OrderStatusEnum.SUBMITTED]: 'circle-check',
  [OrderStatusEnum.PENDING_CANCELLED]: 'spinner',
  [OrderStatusEnum.SUBMITTED_AND_DRAFT]: 'circle-check',
  [OrderStatusEnum.ACCEPTED]: 'circle-check',
  [OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE]: 'plug-circle-exclamation',
  [OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE]: 'triangle-exclamation',
};

export const MaturityStatusBackgroundColor: Record<StatusWithIcon, string> = {
  [OrderStatusEnum.PENDING]: 'rgba(122, 204, 121, 0.2)',
  [OrderStatusEnum.SUBMITTED]: 'rgba(122, 204, 121, 0.2)',
  [OrderStatusEnum.PENDING_CANCELLED]: 'rgba(122, 204, 121, 0.2)',
  [OrderStatusEnum.SUBMITTED_AND_DRAFT]: 'rgba(255, 182, 96, 0.2)',
  [OrderStatusEnum.ACCEPTED]: 'rgba(122, 204, 121, 0.2)',
  [OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE]: 'pink',
  [OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE]: 'purple',
};

export const MaturityStatusColor: Record<StatusWithIcon, string> = {
  [OrderStatusEnum.PENDING]: '#7acc79',
  [OrderStatusEnum.SUBMITTED]: '#7acc79',
  [OrderStatusEnum.PENDING_CANCELLED]: '#7acc79',
  [OrderStatusEnum.SUBMITTED_AND_DRAFT]: '#ffb660',
  [OrderStatusEnum.ACCEPTED]: '#7acc79',
  [OrderStatusEnum.SUBMITTED_INTERNAL_FAILURE]: 'red',
  [OrderStatusEnum.SUBMITTED_EXTERNAL_FAILURE]: 'red',
};

export interface MaturityOrder {
  MATURITY_ID: string;
  MATURITY_AMOUNT: string;
  MATURITY_PRESENT_VALUE?: string;
  MATURITY_DATE: number;
  PUT_DATE?: number;
  MATURITY_DESCRIPTION?: string;
  SECURITY_TYPE?: string;
  PRICE?: string;
  YIELD?: number;
  SPREAD?: number;
  TAX_TREASURY_SPREAD?: number;
  TAX_TREASURY_COUPON?: number;
  TAX_TREASURY_MATURITY_DATE?: number;
  COUPON?: number;
  MATURITY_OVERRIDE_FITCH?: string;
  MATURITY_OVERRIDE_MOODYS?: string;
  MATURITY_OVERRIDE_SANDP?: string;
  MATURITY_OVERRIDE_INSURANCE?: string;
  CALL_DATE_OVERRIDE?: string;
  MANDATORY_TENDER?: string;
  MULTIPLES?: number;

  ORDER_ID: string;
  ORDER_STATUS?: OrderStatusEnum;
  RECONFIRM_ELIGIBLE?: boolean;
  ORDER_TYPE?: string;
  QUANTITY: string;
  SUBMITTED_QUANTITY?: string;
  DEAL_ID?: string;
  SERIES_ID: string;
  SERIES_CODE?: string;
  MINIMUM_ORDER_AMOUNT?: string;
  MAXIMUM_ORDER_AMOUNT?: string;
  IS_AUTO_RESET_ORDER_AMOUNT?: boolean;
  /** Set by server when ACCEPTED order amount is explicitly reduced to zero for cancellation. */
  IS_ZERO_CANCEL_INTENT?: boolean;
  ORDER_AMOUNT_INCREMENT?: number;
  ACCOUNT_CODE: string;
  ZIP_CODE?: string;
  COMMENT?: string;
  PRIORITY_TYPE?: PriorityTypeEnum;
  RETAIL_TYPE?: RetailTypeEnum;
  MUNI_STRIP?: boolean;
  MUNI_AON?: boolean;
  FLEX_MATURITY?: string;
  FLEX_COUPON?: string;
  BOFA_REFERENCE_NUMBER?: number;
  ORIG_REFERENCE_NUMBER?: string;
  IS_CONFIRMED?: boolean;
  ERROR_MESSAGE?: string;

  // Split order fields (grid anchor / split maturity)
  HAS_SPLIT?: boolean;
  IS_SPLIT_MATURITY?: boolean;
  /** @deprecated use LINE_NO — kept for API payloads (splitLineNo) */
  IS_SPLIT?: boolean;
  LINE_NO?: number;
  SPLIT_LINE_NO?: number;
  SPLIT_CONDITIONS_VARIOUS?: boolean;

  // Price change flags set by backend
  COUPON_CHANGED?: boolean;
  YIELD_CHANGED?: boolean;
  PRICE_CHANGED?: boolean;
  MATURITY_AMOUNT_CHANGED?: boolean;
  MATURITY_PRESENT_VALUE_CHANGED?: boolean;

  /** JSON array of sinking fund { date, amount } tuples; non-empty valid parse means term bond */
  SINKING_FUND_SCHEDULE?: string;
}

export enum MuniOrderType {
  INDIVIDUAL_RETAIL = 'INDIVIDUAL_RETAIL',
  PROFESSIONAL_RETAIL = 'PROFESSIONAL_RETAIL',
  OTHER = 'OTHER',
}

export enum PriorityTypeEnum {
  GROUP_NET = 'GROUP_NET',
  NET_DESIGNATED = 'NET_DESIGNATED',
  MEMBER = 'MEMBER',
  NONE = 'NONE',
}

export enum RetailTypeEnum {
  LOCAL = 'LOCAL',
  STATE = 'STATE',
  NATIONAL = 'NATIONAL',
  NONE = 'NONE',
}

/** Quantity cell validation for grid and conditions modal. */
export interface QuantityCellValidationState {
  hasError: boolean;
  hasWarning: boolean;
  tooltip: string | null;
}
